import React, { InputHTMLAttributes, useCallback, useContext, useEffect, useState } from 'react';
import { distinctUntilChanged, pluck } from 'rxjs/operators';

import { DefaultProps } from './types';
import { useField } from './useField';

type RadioGroupProps = DefaultProps;

const RxRadioContext = React.createContext<{
    field: string;
    groupValue?: string;
    onInputChange?(...args: any[]): void;
}>({
    field: 'unknown',
});

export const RxRadioGroup: React.FC<RadioGroupProps> = React.memo((props) => {
    const { onInputChange, formInitialValue, getValueStream } = useField(props);
    const [value, setValue] = useState(formInitialValue);

    useEffect(() => {
        const sub = getValueStream()
            .pipe(pluck(props.field), distinctUntilChanged())
            .subscribe((x) => setValue(x));
        return () => sub.unsubscribe();
    }, [props.field, getValueStream]);

    return (
        <RxRadioContext.Provider value={{ field: props.field, groupValue: value, onInputChange }}>
            {props.children}
        </RxRadioContext.Provider>
    );
});

type RxRadioProps = {
    value: any;
};

export const RxRadio: React.FC<RxRadioProps & InputHTMLAttributes<any>> = (props) => {
    const { value, ...rest } = props;
    const { field, groupValue, onInputChange } = useContext(RxRadioContext);
    /**
     * Adding this proxy to allow the radio input to represent
     * more values than just booleans
     */
    const proxy = useCallback(() => {
        onInputChange &&
            onInputChange({
                target: {
                    value: value,
                    type: 'radio',
                },
            });
    }, [onInputChange, value]);
    return (
        <input
            {...(rest as any)}
            onChange={proxy}
            // onBlur={onInputBlur}
            checked={value === groupValue}
            name={field}
            type="radio"
        />
    );
};

export const RadioGroup = RxRadioGroup;
export const Radio = RxRadio;
